import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from '../utils/toast';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api`;

const ManagerEvaluationFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const evaluation = location.state?.evaluation; // Get evaluation from navigation state
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [confirmed, setConfirmed] = useState(false);

  // Staff feedback state
  const [staffFeedback, setStaffFeedback] = useState({});

  // Department feedback state
  const [departmentFeedback, setDepartmentFeedback] = useState({
    compliance_readiness: '',
    patient_care_safety: '',
    cost_effectiveness: ''
  });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      if (parsedUser.role !== 'Manager' && parsedUser.role !== 'Reporting Manager') {
        toast.error('Access denied. This page is for Managers only.');
        navigate('/');
        return;
      }
      
      // Check if evaluation data is provided
      if (!evaluation) {
        toast.error('No evaluation data found. Redirecting...');
        navigate('/manager/evaluations');
        return;
      }
      
      fetchTeamMembers(parsedUser.id);
    } else {
      navigate('/signin');
    }
  }, [navigate, evaluation]);

  const fetchTeamMembers = async (managerId) => {
    try {
      const token = localStorage.getItem('token');
      // Use same endpoint as Team Progress Dashboard to get all team members
      const response = await axios.get(`${API}/manager/team-progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Extract team members from team progress data (same as ReportingManagerDashboard)
      const allMembers = response.data.team_progress || [];
      
      // Filter out the reporting manager themselves - they shouldn't evaluate themselves
      const members = allMembers.filter(member => member.user_id !== managerId);
      setTeamMembers(members);
      
      // Initialize staff feedback state
      const initialFeedback = {};
      members.forEach(member => {
        // Use employee_id as the key (matching team progress structure)
        initialFeedback[member.employee_id] = {
          compliance: '',
          skill_gap: '',
          patient_care: ''
        };
      });
      setStaffFeedback(initialFeedback);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching team members:', error);
      if (error.response?.status === 403) {
        toast.error('You are not assigned as a Reporting Manager');
      } else {
        toast.error('Failed to load team members');
      }
      setLoading(false);
    }
  };

  const handleStaffFeedbackChange = (employeeId, category, value) => {
    setStaffFeedback(prev => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        [category]: value
      }
    }));
  };

  const handleDepartmentFeedbackChange = (category, value) => {
    setDepartmentFeedback(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const validateForm = () => {
    // Check all staff members have all ratings
    for (const employeeId in staffFeedback) {
      const feedback = staffFeedback[employeeId];
      if (!feedback.compliance || !feedback.skill_gap || !feedback.patient_care) {
        toast.error('Please rate all staff members across all categories');
        return false;
      }
    }

    // Check department feedback
    if (!departmentFeedback.compliance_readiness || 
        !departmentFeedback.patient_care_safety || 
        !departmentFeedback.cost_effectiveness) {
      toast.error('Please complete all department-level questions');
      return false;
    }

    // Check confirmation
    if (!confirmed) {
      toast.error('Please confirm the accuracy of your feedback');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        manager_id: user.id,
        manager_name: user.full_name || user.name,
        staff_feedback: Object.keys(staffFeedback).map(employeeId => ({
          employee_id: employeeId,
          employee_name: teamMembers.find(m => m.employee_id === employeeId)?.employee_name,
          ratings: staffFeedback[employeeId]
        })),
        department_feedback: departmentFeedback,
        submitted_at: new Date().toISOString()
      };

      await axios.post(`${API}/manager/evaluation-form`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Evaluation form submitted successfully!');
      
      // Reset form
      const initialFeedback = {};
      teamMembers.forEach(member => {
        initialFeedback[member.employee_id] = {
          compliance: '',
          skill_gap: '',
          patient_care: ''
        };
      });
      setStaffFeedback(initialFeedback);
      setDepartmentFeedback({
        compliance_readiness: '',
        patient_care_safety: '',
        cost_effectiveness: ''
      });
      setConfirmed(false);
    } catch (error) {
      console.error('Error submitting evaluation:', error);
      toast.error('Failed to submit evaluation form');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading team data...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8 max-w-7xl mx-auto bg-gray-50 min-h-screen">
        {/* Header with Back Button */}
        <div className="mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/manager/evaluations')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to My Evaluations
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Staff and Department Feedback (Manager Evaluation)
              </h1>
              <p className="text-gray-600 text-sm">
                This form captures staff-level feedback on compliance, skill gaps, and patient care, 
                and department-level feedback on compliance & readiness, patient care & safety, and cost effectiveness.
              </p>
            </div>
            {evaluation && (
              <Badge className="bg-blue-100 text-blue-800">
                Evaluation #{evaluation.instance_id.slice(0, 8)}
              </Badge>
            )}
          </div>
        </div>

        {/* Subchapter 1: Staff Feedback Table */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Subchapter 1: Staff Feedback Table
          </h2>
          <p className="text-gray-600 text-sm mb-4">
            Rate each staff member across the listed categories based on their performance and contribution.
          </p>

          {teamMembers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No team members found. You need to have staff members assigned to you to complete this evaluation.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left p-3 font-bold text-gray-900 border-r border-gray-300">
                      Staff
                    </th>
                    <th className="text-left p-3 font-bold text-gray-900 border-r border-gray-300">
                      <div>Compliance</div>
                      <div className="text-xs font-normal text-gray-600 mt-1">
                        (How well does the staff adhere to hospital policies, SOPs, and NABH standards?)
                      </div>
                    </th>
                    <th className="text-left p-3 font-bold text-gray-900 border-r border-gray-300">
                      <div>Skill Gap</div>
                      <div className="text-xs font-normal text-gray-600 mt-1">
                        (To what extent does the staff show gaps in skills or training needed to perform their role effectively?)
                      </div>
                    </th>
                    <th className="text-left p-3 font-bold text-gray-900">
                      <div>Patient care</div>
                      <div className="text-xs font-normal text-gray-600 mt-1">
                        (How effectively does the staff contribute to positive patient outcomes and safe patient care?)
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.map((member, index) => (
                    <tr key={member.employee_id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="p-3 font-medium text-gray-900 border-r border-gray-200">
                        <div>
                          <div className="font-semibold">{member.name}</div>
                          <div className="text-xs text-gray-600">{member.job_function || member.department}</div>
                        </div>
                      </td>
                      <td className="p-3 border-r border-gray-200">
                        <Select
                          value={staffFeedback[member.employee_id]?.compliance}
                          onValueChange={(value) => handleStaffFeedbackChange(member.employee_id, 'compliance', value)}
                        >
                          <SelectTrigger className="w-full bg-white">
                            <SelectValue placeholder="Choose" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="4">Excellent (4)</SelectItem>
                            <SelectItem value="3">Good (3)</SelectItem>
                            <SelectItem value="2">Needs Improvement (2)</SelectItem>
                            <SelectItem value="1">Poor (1)</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3 border-r border-gray-200">
                        <Select
                          value={staffFeedback[member.employee_id]?.skill_gap}
                          onValueChange={(value) => handleStaffFeedbackChange(member.employee_id, 'skill_gap', value)}
                        >
                          <SelectTrigger className="w-full bg-white">
                            <SelectValue placeholder="Choose" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="4">None (4)</SelectItem>
                            <SelectItem value="3">Minor (3)</SelectItem>
                            <SelectItem value="2">Moderate (2)</SelectItem>
                            <SelectItem value="1">Significant (1)</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3">
                        <Select
                          value={staffFeedback[member.employee_id]?.patient_care}
                          onValueChange={(value) => handleStaffFeedbackChange(member.employee_id, 'patient_care', value)}
                        >
                          <SelectTrigger className="w-full bg-white">
                            <SelectValue placeholder="Choose" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="4">Strongly Positive (4)</SelectItem>
                            <SelectItem value="3">Positive (3)</SelectItem>
                            <SelectItem value="2">Neutral (2)</SelectItem>
                            <SelectItem value="1">Needs Improvement (1)</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Subchapter 2: Department-Level Questionnaire */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Subchapter 2: Department-Level Questionnaire
          </h2>
          <p className="text-gray-600 text-sm mb-6">
            Evaluate the overall performance of your department across compliance, patient care, teamwork, and efficiency.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Compliance & Readiness */}
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Compliance & Readiness</h3>
              <p className="text-sm text-gray-600 mb-3">
                Compared to the last quarter, how effectively has your department strengthened its compliance 
                with standards and improved overall readiness for audits?
              </p>
              <Select
                value={departmentFeedback.compliance_readiness}
                onValueChange={(value) => handleDepartmentFeedbackChange('compliance_readiness', value)}
              >
                <SelectTrigger className="w-full bg-white">
                  <SelectValue placeholder="Choose" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">Excellent improvement (4)</SelectItem>
                  <SelectItem value="3">Good improvement (3)</SelectItem>
                  <SelectItem value="2">Needs Improvement (2)</SelectItem>
                  <SelectItem value="1">Poor (1)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Patient Care & Safety */}
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Patient Care & Safety</h3>
              <p className="text-sm text-gray-600 mb-3">
                How well is your department ensuring safe, patient-centered care that consistently 
                supports positive outcomes?
              </p>
              <Select
                value={departmentFeedback.patient_care_safety}
                onValueChange={(value) => handleDepartmentFeedbackChange('patient_care_safety', value)}
              >
                <SelectTrigger className="w-full bg-white">
                  <SelectValue placeholder="Choose" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">Very Strong (4)</SelectItem>
                  <SelectItem value="3">Strong (3)</SelectItem>
                  <SelectItem value="2">Moderate (2)</SelectItem>
                  <SelectItem value="1">Needs Improvement (1)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Cost-Effectiveness */}
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Cost-Effectiveness</h3>
              <p className="text-sm text-gray-600 mb-3">
                To what extent has the training and process improvements in your department reduced 
                staff time and improved cost efficiency?
              </p>
              <Select
                value={departmentFeedback.cost_effectiveness}
                onValueChange={(value) => handleDepartmentFeedbackChange('cost_effectiveness', value)}
              >
                <SelectTrigger className="w-full bg-white">
                  <SelectValue placeholder="Choose" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">Very effective (4)</SelectItem>
                  <SelectItem value="3">Effective (3)</SelectItem>
                  <SelectItem value="2">Slightly effective (2)</SelectItem>
                  <SelectItem value="1">Not effective (1)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Subchapter 3: Confirmation */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Subchapter 3: Confirmation
          </h2>
          
          <div className="flex items-center space-x-3">
            <Checkbox
              id="confirmation"
              checked={confirmed}
              onCheckedChange={setConfirmed}
            />
            <label
              htmlFor="confirmation"
              className="text-gray-700 cursor-pointer select-none text-sm"
            >
              I confirm this feedback is accurate to the best of my knowledge.
            </label>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-start">
          <Button
            onClick={handleSubmit}
            disabled={submitting || teamMembers.length === 0}
            className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-2"
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ManagerEvaluationFormPage;
